// ---------------------------------------------------------------------------
// Narrow-phase geometrica: triangulo contra triangulo.
//
// INTERSECCAO (Moller 1997, "A Fast Triangle-Triangle Intersection
// Test"). Escolhido em vez de SAT (13 eixos) porque:
//   - da o SEGMENTO de interseccao, nao so um booleano. Precisamos dele
//     para o ponto representativo (camara, viewpoint BCF) e para a
//     caixa da zona de conflito.
//   - o caso separado por plano (a esmagadora maioria dos pares) sai em
//     3 produtos internos, mais barato que os 13 eixos do SAT.
// Diferenca face ao artigo original: as normais sao NORMALIZADAS. O
// artigo trabalha com normais nao unitarias, o que faz as "distancias"
// escalarem com a area do triangulo e torna impossivel usar uma
// tolerancia em metros. Custa um sqrt por triangulo e paga-se em
// previsibilidade.
//
// TOCAR NAO E PENETRAR. Duas variantes do mesmo nucleo:
//   - `triTriTouch`: predicado geometrico classico. Verdadeiro quando os
//     triangulos partilham QUALQUER ponto (inclui coplanares
//     sobrepostos e aresta partilhada).
//   - `triTriPenetrate`: exige que cada triangulo ATRAVESSE o plano do
//     outro (vertices dos dois lados, fora da tolerancia). E o que a
//     deteccao de conflitos usa. Rejeita, por construcao:
//       * aresta partilhada (dois vertices no plano, o terceiro so de um
//         lado): a parede que assenta na laje nao e um conflito;
//       * vertice partilhado;
//       * faces coplanares encostadas (contacto de volume nulo);
//       * paralelos proximos.
//     Estes casos nao tem volume de interpenetracao. Reporta-los seria
//     inundar o utilizador com o modelo inteiro.
//
// DISTANCIA (para a folga/clearance): minimo exacto entre dois
// triangulos = minimo das 6 distancias segmento-triangulo (3 arestas de
// cada um contra o outro triangulo). Fazer so aresta-aresta mais
// vertice-triangulo e a armadilha classica: uma agulha a espetar pelo
// meio de um triangulo nao toca nenhuma aresta e nenhum vertice fica
// perto, e a distancia daria > 0 com os solidos a atravessarem-se. A
// distancia segmento-triangulo inclui o teste de travessia e devolve 0
// nesse caso.
//
// Triangulos degenerados (area zero) devolvem "sem interseccao" em vez
// de rebentarem: a tesselacao do IFC esta cheia deles.
// ---------------------------------------------------------------------------

export type V3 = ArrayLike<number>

/** Tolerancia geometrica por omissao, em metros (1 nm). O chamador
 *  deve escalar com a grandeza das coordenadas: ver `epsForScale`. */
export const TRI_EPS = 1e-9

/** Tolerancia adequada a coordenadas ate `scale` metros da origem do
 *  referencial de trabalho. As posicoes vem em Float32 (a recolha de
 *  triangulos guarda-as assim), cujo passo de quantizacao a 1000 m e
 *  ~6e-5 m: usar uma tolerancia abaixo do ruido faz duas faces
 *  coplanares parecerem cruzadas. */
export function epsForScale(scale: number): number {
  const s = Number.isFinite(scale) ? Math.abs(scale) : 0
  return Math.max(TRI_EPS, 1e-7 * s)
}

/** Segmento de interseccao entre dois triangulos. */
export interface TriTriSegment {
  p: [number, number, number]
  q: [number, number, number]
}

// Rascunhos reutilizados: o nucleo corre milhoes de vezes e nao pode
// alocar. Nao ha reentrancia (tudo single-threaded, sem async no meio).
const _dA = new Float64Array(3)
const _dB = new Float64Array(3)
const _n1 = new Float64Array(3)
const _n2 = new Float64Array(3)
const _segA = new Float64Array(6)
const _segB = new Float64Array(6)

function normalOf(
  a: V3,
  b: V3,
  c: V3,
  out: Float64Array,
): number {
  const ux = b[0] - a[0]
  const uy = b[1] - a[1]
  const uz = b[2] - a[2]
  const vx = c[0] - a[0]
  const vy = c[1] - a[1]
  const vz = c[2] - a[2]
  const nx = uy * vz - uz * vy
  const ny = uz * vx - ux * vz
  const nz = ux * vy - uy * vx
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz)
  if (!(len > 0)) {
    out[0] = 0
    out[1] = 0
    out[2] = 0
    return 0
  }
  out[0] = nx / len
  out[1] = ny / len
  out[2] = nz / len
  return len
}

function signedDist(n: Float64Array, origin: V3, p: V3, eps: number): number {
  const d =
    n[0] * (p[0] - origin[0]) +
    n[1] * (p[1] - origin[1]) +
    n[2] * (p[2] - origin[2])
  return Math.abs(d) <= eps ? 0 : d
}

/** Interseccao do triangulo (p0,p1,p2) com o plano do outro, dadas as
 *  distancias com sinal ja arredondadas. Escreve ate 2 pontos em `out`
 *  e devolve quantos encontrou. */
function triPlaneSegment(
  p0: V3,
  p1: V3,
  p2: V3,
  d: Float64Array,
  out: Float64Array,
): number {
  let count = 0
  const push = (x: number, y: number, z: number): void => {
    if (count >= 2) return
    out[count * 3] = x
    out[count * 3 + 1] = y
    out[count * 3 + 2] = z
    count++
  }
  const v = [p0, p1, p2]
  for (let i = 0; i < 3; i++) {
    if (d[i] === 0) push(v[i][0], v[i][1], v[i][2])
  }
  for (let i = 0; i < 3; i++) {
    const j = (i + 1) % 3
    if (d[i] * d[j] < 0) {
      const t = d[i] / (d[i] - d[j])
      push(
        v[i][0] + t * (v[j][0] - v[i][0]),
        v[i][1] + t * (v[j][1] - v[i][1]),
        v[i][2] + t * (v[j][2] - v[i][2]),
      )
    }
  }
  return count
}

/** Projeccao 2D deitando fora o eixo dominante de `n`. */
function dropAxis(n: Float64Array): number {
  const ax = Math.abs(n[0])
  const ay = Math.abs(n[1])
  const az = Math.abs(n[2])
  if (ax >= ay && ax >= az) return 0
  return ay >= az ? 1 : 2
}

function orient2d(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): number {
  return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax)
}

function pointInTri2d(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
): boolean {
  const d1 = orient2d(ax, ay, bx, by, px, py)
  const d2 = orient2d(bx, by, cx, cy, px, py)
  const d3 = orient2d(cx, cy, ax, ay, px, py)
  const neg = d1 < 0 || d2 < 0 || d3 < 0
  const pos = d1 > 0 || d2 > 0 || d3 > 0
  return !(neg && pos)
}

function onSegment2d(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  px: number,
  py: number,
): boolean {
  return (
    Math.min(ax, bx) <= px &&
    px <= Math.max(ax, bx) &&
    Math.min(ay, by) <= py &&
    py <= Math.max(ay, by)
  )
}

/** Interseccao de dois segmentos 2D, INCLUINDO contactos e casos
 *  colineares: dois triangulos coplanares que so partilham aresta
 *  contam como "a tocar". */
function seg2dIntersect(
  a0x: number,
  a0y: number,
  a1x: number,
  a1y: number,
  b0x: number,
  b0y: number,
  b1x: number,
  b1y: number,
): boolean {
  const d1 = orient2d(a0x, a0y, a1x, a1y, b0x, b0y)
  const d2 = orient2d(a0x, a0y, a1x, a1y, b1x, b1y)
  const d3 = orient2d(b0x, b0y, b1x, b1y, a0x, a0y)
  const d4 = orient2d(b0x, b0y, b1x, b1y, a1x, a1y)
  if (((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0)) &&
      d1 !== 0 && d2 !== 0 && d3 !== 0 && d4 !== 0) {
    return true
  }
  if (d1 === 0 && onSegment2d(a0x, a0y, a1x, a1y, b0x, b0y)) return true
  if (d2 === 0 && onSegment2d(a0x, a0y, a1x, a1y, b1x, b1y)) return true
  if (d3 === 0 && onSegment2d(b0x, b0y, b1x, b1y, a0x, a0y)) return true
  if (d4 === 0 && onSegment2d(b0x, b0y, b1x, b1y, a1x, a1y)) return true
  return false
}

/** Sobreposicao de dois triangulos COPLANARES, projectados no plano
 *  dominante. */
function coplanarOverlap(
  n: Float64Array,
  a0: V3,
  a1: V3,
  a2: V3,
  b0: V3,
  b1: V3,
  b2: V3,
): boolean {
  const drop = dropAxis(n)
  const i0 = drop === 0 ? 1 : 0
  const i1 = drop === 2 ? 1 : 2
  const ax = [a0[i0], a1[i0], a2[i0]]
  const ay = [a0[i1], a1[i1], a2[i1]]
  const bx = [b0[i0], b1[i0], b2[i0]]
  const by = [b0[i1], b1[i1], b2[i1]]
  for (let i = 0; i < 3; i++) {
    const j = (i + 1) % 3
    for (let k = 0; k < 3; k++) {
      const l = (k + 1) % 3
      if (
        seg2dIntersect(
          ax[i], ay[i], ax[j], ay[j],
          bx[k], by[k], bx[l], by[l],
        )
      ) {
        return true
      }
    }
  }
  // Um contido no outro (sem cruzamento de arestas).
  if (pointInTri2d(ax[0], ay[0], bx[0], by[0], bx[1], by[1], bx[2], by[2])) {
    return true
  }
  if (pointInTri2d(bx[0], by[0], ax[0], ay[0], ax[1], ay[1], ax[2], ay[2])) {
    return true
  }
  return false
}

/** Resultado do nucleo: 0 sem interseccao, 1 segmento em `out`,
 *  2 coplanares sobrepostos (sem segmento). */
function triTriCore(
  a0: V3,
  a1: V3,
  a2: V3,
  b0: V3,
  b1: V3,
  b2: V3,
  eps: number,
  requirePenetration: boolean,
  out: Float64Array | null,
): number {
  if (normalOf(b0, b1, b2, _n2) === 0) return 0
  _dA[0] = signedDist(_n2, b0, a0, eps)
  _dA[1] = signedDist(_n2, b0, a1, eps)
  _dA[2] = signedDist(_n2, b0, a2, eps)
  const aMin = Math.min(_dA[0], _dA[1], _dA[2])
  const aMax = Math.max(_dA[0], _dA[1], _dA[2])
  if (aMin > 0 || aMax < 0) return 0
  if (requirePenetration && !(aMin < 0 && aMax > 0)) return 0

  if (normalOf(a0, a1, a2, _n1) === 0) return 0
  _dB[0] = signedDist(_n1, a0, b0, eps)
  _dB[1] = signedDist(_n1, a0, b1, eps)
  _dB[2] = signedDist(_n1, a0, b2, eps)
  const bMin = Math.min(_dB[0], _dB[1], _dB[2])
  const bMax = Math.max(_dB[0], _dB[1], _dB[2])
  if (bMin > 0 || bMax < 0) return 0
  if (requirePenetration && !(bMin < 0 && bMax > 0)) return 0

  // Coplanares: volume de interseccao nulo, nunca e penetracao.
  const dx = _n1[1] * _n2[2] - _n1[2] * _n2[1]
  const dy = _n1[2] * _n2[0] - _n1[0] * _n2[2]
  const dz = _n1[0] * _n2[1] - _n1[1] * _n2[0]
  const dirMax = Math.max(Math.abs(dx), Math.abs(dy), Math.abs(dz))
  if (dirMax < 1e-12) {
    if (requirePenetration) return 0
    return coplanarOverlap(_n1, a0, a1, a2, b0, b1, b2) ? 2 : 0
  }

  const nA = triPlaneSegment(a0, a1, a2, _dA, _segA)
  const nB = triPlaneSegment(b0, b1, b2, _dB, _segB)
  if (nA < 2 || nB < 2) return 0

  // Parametro ao longo da recta de interseccao: a coordenada do eixo
  // dominante da direccao. Ambos os segmentos vivem nessa recta.
  const k = Math.abs(dx) >= Math.abs(dy)
    ? (Math.abs(dx) >= Math.abs(dz) ? 0 : 2)
    : (Math.abs(dy) >= Math.abs(dz) ? 1 : 2)
  const ta0 = _segA[k]
  const ta1 = _segA[3 + k]
  const tb0 = _segB[k]
  const tb1 = _segB[3 + k]
  const aLo = Math.min(ta0, ta1)
  const aHi = Math.max(ta0, ta1)
  const bLo = Math.min(tb0, tb1)
  const bHi = Math.max(tb0, tb1)
  const lo = Math.max(aLo, bLo)
  const hi = Math.min(aHi, bHi)
  if (requirePenetration ? !(hi > lo) : !(hi >= lo)) return 0

  if (out) {
    const span = ta1 - ta0
    const at = (t: number, comp: number): number => {
      if (span === 0) return _segA[comp]
      const u = (t - ta0) / span
      return _segA[comp] + u * (_segA[3 + comp] - _segA[comp])
    }
    out[0] = at(lo, 0)
    out[1] = at(lo, 1)
    out[2] = at(lo, 2)
    out[3] = at(hi, 0)
    out[4] = at(hi, 1)
    out[5] = at(hi, 2)
  }
  return 1
}

/** Predicado geometrico classico: os triangulos partilham algum ponto?
 *  Inclui coplanares sobrepostos e contactos por aresta ou vertice. */
export function triTriTouch(
  a0: V3,
  a1: V3,
  a2: V3,
  b0: V3,
  b1: V3,
  b2: V3,
  eps: number = TRI_EPS,
): boolean {
  return triTriCore(a0, a1, a2, b0, b1, b2, eps, false, null) !== 0
}

/** Interpenetracao real: cada triangulo atravessa o plano do outro.
 *  Devolve o segmento de interseccao (em coordenadas de entrada) ou
 *  null. Aresta ou vertice partilhados, coplanares e paralelos
 *  proximos devolvem null. */
export function triTriPenetrate(
  a0: V3,
  a1: V3,
  a2: V3,
  b0: V3,
  b1: V3,
  b2: V3,
  eps: number = TRI_EPS,
): TriTriSegment | null {
  const out = new Float64Array(6)
  if (triTriCore(a0, a1, a2, b0, b1, b2, eps, true, out) !== 1) return null
  return {
    p: [out[0], out[1], out[2]],
    q: [out[3], out[4], out[5]],
  }
}

/** Variante sem alocacao: escreve o segmento em `out` (6 numeros) e
 *  devolve true. Usada pelo motor no ciclo quente. */
export function triTriPenetrateInto(
  a0: V3,
  a1: V3,
  a2: V3,
  b0: V3,
  b1: V3,
  b2: V3,
  eps: number,
  out: Float64Array,
): boolean {
  return triTriCore(a0, a1, a2, b0, b1, b2, eps, true, out) === 1
}

// ---------------------------------------------------------------------------
// Distancias (Ericson, "Real-Time Collision Detection", cap. 5).
// ---------------------------------------------------------------------------

const _cp = new Float64Array(3)

/** Ponto do triangulo mais proximo de `p`, escrito em `out`. */
export function closestPointOnTriangle(
  p: V3,
  a: V3,
  b: V3,
  c: V3,
  out: Float64Array,
): void {
  const abx = b[0] - a[0]
  const aby = b[1] - a[1]
  const abz = b[2] - a[2]
  const acx = c[0] - a[0]
  const acy = c[1] - a[1]
  const acz = c[2] - a[2]
  const apx = p[0] - a[0]
  const apy = p[1] - a[1]
  const apz = p[2] - a[2]
  const d1 = abx * apx + aby * apy + abz * apz
  const d2 = acx * apx + acy * apy + acz * apz
  if (d1 <= 0 && d2 <= 0) {
    out[0] = a[0]
    out[1] = a[1]
    out[2] = a[2]
    return
  }
  const bpx = p[0] - b[0]
  const bpy = p[1] - b[1]
  const bpz = p[2] - b[2]
  const d3 = abx * bpx + aby * bpy + abz * bpz
  const d4 = acx * bpx + acy * bpy + acz * bpz
  if (d3 >= 0 && d4 <= d3) {
    out[0] = b[0]
    out[1] = b[1]
    out[2] = b[2]
    return
  }
  const vc = d1 * d4 - d3 * d2
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    const v = d1 / (d1 - d3)
    out[0] = a[0] + v * abx
    out[1] = a[1] + v * aby
    out[2] = a[2] + v * abz
    return
  }
  const cpx = p[0] - c[0]
  const cpy = p[1] - c[1]
  const cpz = p[2] - c[2]
  const d5 = abx * cpx + aby * cpy + abz * cpz
  const d6 = acx * cpx + acy * cpy + acz * cpz
  if (d6 >= 0 && d5 <= d6) {
    out[0] = c[0]
    out[1] = c[1]
    out[2] = c[2]
    return
  }
  const vb = d5 * d2 - d1 * d6
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    const w = d2 / (d2 - d6)
    out[0] = a[0] + w * acx
    out[1] = a[1] + w * acy
    out[2] = a[2] + w * acz
    return
  }
  const va = d3 * d6 - d5 * d4
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    const w = (d4 - d3) / (d4 - d3 + (d5 - d6))
    out[0] = b[0] + w * (c[0] - b[0])
    out[1] = b[1] + w * (c[1] - b[1])
    out[2] = b[2] + w * (c[2] - b[2])
    return
  }
  const denom = 1 / (va + vb + vc)
  const v = vb * denom
  const w = vc * denom
  out[0] = a[0] + abx * v + acx * w
  out[1] = a[1] + aby * v + acy * w
  out[2] = a[2] + abz * v + acz * w
}

export function pointTriangleDistance(p: V3, a: V3, b: V3, c: V3): number {
  closestPointOnTriangle(p, a, b, c, _cp)
  const dx = p[0] - _cp[0]
  const dy = p[1] - _cp[1]
  const dz = p[2] - _cp[2]
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/** Distancia minima entre dois segmentos. Quando `out` vem, recebe os
 *  dois pontos mais proximos (6 numeros). */
export function segmentSegmentDistance(
  p1: V3,
  q1: V3,
  p2: V3,
  q2: V3,
  out?: Float64Array,
): number {
  const d1x = q1[0] - p1[0]
  const d1y = q1[1] - p1[1]
  const d1z = q1[2] - p1[2]
  const d2x = q2[0] - p2[0]
  const d2y = q2[1] - p2[1]
  const d2z = q2[2] - p2[2]
  const rx = p1[0] - p2[0]
  const ry = p1[1] - p2[1]
  const rz = p1[2] - p2[2]
  const a = d1x * d1x + d1y * d1y + d1z * d1z
  const e = d2x * d2x + d2y * d2y + d2z * d2z
  const f = d2x * rx + d2y * ry + d2z * rz
  let s: number
  let t: number
  const EPS = 1e-20
  if (a <= EPS && e <= EPS) {
    s = 0
    t = 0
  } else if (a <= EPS) {
    s = 0
    t = clamp01(f / e)
  } else {
    const c = d1x * rx + d1y * ry + d1z * rz
    if (e <= EPS) {
      t = 0
      s = clamp01(-c / a)
    } else {
      const b = d1x * d2x + d1y * d2y + d1z * d2z
      const denom = a * e - b * b
      s = denom !== 0 ? clamp01((b * f - c * e) / denom) : 0
      t = (b * s + f) / e
      if (t < 0) {
        t = 0
        s = clamp01(-c / a)
      } else if (t > 1) {
        t = 1
        s = clamp01((b - c) / a)
      }
    }
  }
  const c1x = p1[0] + d1x * s
  const c1y = p1[1] + d1y * s
  const c1z = p1[2] + d1z * s
  const c2x = p2[0] + d2x * t
  const c2y = p2[1] + d2y * t
  const c2z = p2[2] + d2z * t
  if (out) {
    out[0] = c1x
    out[1] = c1y
    out[2] = c1z
    out[3] = c2x
    out[4] = c2y
    out[5] = c2z
  }
  const dx = c1x - c2x
  const dy = c1y - c2y
  const dz = c1z - c2z
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/** O segmento atravessa o triangulo? (Moller-Trumbore com o parametro
 *  limitado a [0,1].) Quando `out` vem, recebe o ponto de travessia. */
export function segmentCrossesTriangle(
  p: V3,
  q: V3,
  a: V3,
  b: V3,
  c: V3,
  out: Float64Array | null,
): boolean {
  const dx = q[0] - p[0]
  const dy = q[1] - p[1]
  const dz = q[2] - p[2]
  const e1x = b[0] - a[0]
  const e1y = b[1] - a[1]
  const e1z = b[2] - a[2]
  const e2x = c[0] - a[0]
  const e2y = c[1] - a[1]
  const e2z = c[2] - a[2]
  const px = dy * e2z - dz * e2y
  const py = dz * e2x - dx * e2z
  const pz = dx * e2y - dy * e2x
  const det = e1x * px + e1y * py + e1z * pz
  if (Math.abs(det) < 1e-18) return false
  const inv = 1 / det
  const tx = p[0] - a[0]
  const ty = p[1] - a[1]
  const tz = p[2] - a[2]
  const u = (tx * px + ty * py + tz * pz) * inv
  if (u < 0 || u > 1) return false
  const qx = ty * e1z - tz * e1y
  const qy = tz * e1x - tx * e1z
  const qz = tx * e1y - ty * e1x
  const v = (dx * qx + dy * qy + dz * qz) * inv
  if (v < 0 || u + v > 1) return false
  const t = (e2x * qx + e2y * qy + e2z * qz) * inv
  if (t < 0 || t > 1) return false
  if (out) {
    out[0] = p[0] + dx * t
    out[1] = p[1] + dy * t
    out[2] = p[2] + dz * t
  }
  return true
}

const _ss = new Float64Array(6)

/** Distancia minima entre um segmento e um triangulo. 0 quando o
 *  segmento atravessa o triangulo. */
export function segmentTriangleDistance(
  p: V3,
  q: V3,
  a: V3,
  b: V3,
  c: V3,
  out?: Float64Array,
): number {
  if (segmentCrossesTriangle(p, q, a, b, c, _ss)) {
    if (out) {
      out[0] = _ss[0]
      out[1] = _ss[1]
      out[2] = _ss[2]
      out[3] = _ss[0]
      out[4] = _ss[1]
      out[5] = _ss[2]
    }
    return 0
  }
  let best = Infinity
  const consider = (
    d: number,
    x1: number,
    y1: number,
    z1: number,
    x2: number,
    y2: number,
    z2: number,
  ): void => {
    if (d >= best) return
    best = d
    if (out) {
      out[0] = x1
      out[1] = y1
      out[2] = z1
      out[3] = x2
      out[4] = y2
      out[5] = z2
    }
  }
  const dist = (u: V3): number => {
    const dx = u[0] - _cp[0]
    const dy = u[1] - _cp[1]
    const dz = u[2] - _cp[2]
    return Math.sqrt(dx * dx + dy * dy + dz * dz)
  }
  closestPointOnTriangle(p, a, b, c, _cp)
  consider(dist(p), p[0], p[1], p[2], _cp[0], _cp[1], _cp[2])
  closestPointOnTriangle(q, a, b, c, _cp)
  consider(dist(q), q[0], q[1], q[2], _cp[0], _cp[1], _cp[2])
  const tri = [a, b, c]
  for (let i = 0; i < 3; i++) {
    const j = (i + 1) % 3
    const d = segmentSegmentDistance(p, q, tri[i], tri[j], _ss)
    consider(d, _ss[0], _ss[1], _ss[2], _ss[3], _ss[4], _ss[5])
  }
  return best
}

/** Distancia minima entre dois triangulos. 0 quando se intersectam.
 *  Quando `out` vem, recebe os dois pontos mais proximos. */
export function triTriDistance(
  a0: V3,
  a1: V3,
  a2: V3,
  b0: V3,
  b1: V3,
  b2: V3,
  out?: Float64Array,
): number {
  const A = [a0, a1, a2]
  const B = [b0, b1, b2]
  let best = Infinity
  const tmp = out ? new Float64Array(6) : undefined
  for (let i = 0; i < 3; i++) {
    const j = (i + 1) % 3
    const d = segmentTriangleDistance(A[i], A[j], b0, b1, b2, tmp)
    if (d < best) {
      best = d
      if (out && tmp) out.set(tmp)
      if (best === 0) return 0
    }
  }
  for (let i = 0; i < 3; i++) {
    const j = (i + 1) % 3
    const d = segmentTriangleDistance(B[i], B[j], a0, a1, a2, tmp)
    if (d < best) {
      best = d
      if (out && tmp) {
        // Ordem A depois B, sempre.
        out[0] = tmp[3]
        out[1] = tmp[4]
        out[2] = tmp[5]
        out[3] = tmp[0]
        out[4] = tmp[1]
        out[5] = tmp[2]
      }
      if (best === 0) return 0
    }
  }
  return best
}
